import type { ReactElement } from "react";
import { AdBanner, AdConsentLink, SocialLinks } from "@suite/ui";
/** A static landing page — no persisted state. */
export const STORAGE_KEY = "home:none";

export const CONTACT_EMAIL = "hello@ottercraft.dev";

interface Tool {
  name: string;
  /** One line: what it is. */
  tagline: string;
  /** One more line: how it feels to use. Two lines is the whole budget — a
      card is a door, not a brochure, and the tool's own page explains itself. */
  detail: string;
  /** Three or four words each; they carry what a paragraph used to. */
  does: string[];
  href: string;
  host: string;
  icon: ReactElement;
  accent: Accent;
}

type Accent = "teal" | "pine" | "wood" | "sage";

/** One tint per tool, so four cards read as four things at a glance. */
const ACCENTS: Record<Accent, string> = {
  teal: "border-teal/35 bg-teal/12 text-teal-deep",
  pine: "border-pine/25 bg-pine/10 text-pine",
  wood: "border-wood/30 bg-wood/12 text-wood",
  sage: "border-sage-line/45 bg-sage/25 text-sage-deep",
};

const TOOLS: Tool[] = [
  {
    name: "OtterDrop",
    tagline: "Send files to the other devices on your Wi-Fi.",
    detail:
      "Open it on your phone and on your laptop. They find each other, and the file goes straight across.",
    does: ["Phone to laptop", "No size limit", "Nothing uploaded"],
    href: "https://drop.ottercraft.dev",
    host: "drop.ottercraft.dev",
    accent: "teal",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15V4" />
        <path d="m7 9 5-5 5 5" />
        <path d="M4 14c2.5 1.6 5 1.6 8 0s5.5-1.6 8 0" />
        <path d="M4 19c2.5 1.6 5 1.6 8 0s5.5-1.6 8 0" />
      </svg>
    ),
  },
  {
    name: "OtterPDF",
    tagline: "Merge, split, rotate, fill in and sign PDFs.",
    detail:
      "You get a thumbnail of every page. Put them in order, turn the sideways ones, then save.",
    does: ["Merge", "Split", "Rotate", "Fill & sign"],
    href: "https://pdf.ottercraft.dev",
    host: "pdf.ottercraft.dev",
    accent: "pine",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
        <path d="M8.5 13h1.2a1.3 1.3 0 0 1 0 2.6H8.5V13zm0 0v5" />
        <path d="M13.5 13v5h1a1.6 1.6 0 0 0 0-3.2" />
      </svg>
    ),
  },
  {
    name: "OtterStudio",
    tagline: "Trim, layer and title video, then export an MP4.",
    detail:
      "Cut what you don't want, put a second video in the corner, add captions, clean up the sound.",
    does: ["Trim", "Picture-in-picture", "Titles", "Reels & Shorts"],
    href: "https://studio.ottercraft.dev",
    host: "studio.ottercraft.dev",
    accent: "wood",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18" />
        <path d="M8 5v5M16 5v5" />
        <path d="m11 13 3.5 2-3.5 2z" />
      </svg>
    ),
  },
  {
    name: "OtterQR",
    tagline: "Make a QR code, style it, drop your logo in.",
    detail:
      "For a link, your Wi-Fi or a contact card. Pick a shape and colours, then save a PNG or a vector SVG.",
    does: ["Link", "Wi-Fi", "Logo in the middle", "PNG & SVG"],
    href: "https://qr.ottercraft.dev",
    host: "qr.ottercraft.dev",
    accent: "sage",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <path d="M14 14h3v3h-3zM18 18h3v3h-3z" />
      </svg>
    ),
  },
];

const GUIDES: { title: string; blurb: string; href: string }[] = [
  {
    title: "Merge PDF files without uploading them",
    blurb: "Combine a few PDFs into one, in the order you want.",
    href: "/guides/merge-pdf-files-in-your-browser",
  },
  {
    title: "Split a PDF or extract specific pages",
    blurb: "Get pages 3 to 7 out of a long report as their own file.",
    href: "/guides/split-pdf-extract-pages",
  },
  {
    title: "Rotate PDF pages the right way up",
    blurb: "Fix a sideways scan so it stays fixed in every viewer.",
    href: "/guides/rotate-pdf-pages",
  },
  {
    title: "Share files between your phone and computer",
    blurb: "Photos and documents across devices on the same Wi-Fi, no cable.",
    href: "/guides/share-files-between-phone-and-computer",
  },
  {
    title: "Trim a video without uploading it",
    blurb: "Cut the start and end off, or take a piece out of the middle, and save an MP4.",
    href: "/guides/trim-video-in-your-browser",
  },
  {
    title: "Put a reaction cam in the corner of a video",
    blurb: "Two videos playing together, one small over the other, exported as one file.",
    href: "/guides/reaction-video-picture-in-picture",
  },
  {
    title: "Add titles and captions to a video",
    blurb: "Words on the picture, timed on the timeline, with a look, a box and a fade.",
    href: "/guides/add-text-to-a-video",
  },
  {
    title: "Turn a landscape video into a Reel, TikTok or Short",
    blurb: "One button makes the frame 1080×1920, and the footage keeps filling it.",
    href: "/guides/vertical-video-for-reels-tiktok-shorts",
  },
  {
    title: "Make a QR code for a link",
    blurb: "A code that points straight at your page, with no redirect in the middle.",
    href: "/guides/make-a-qr-code-for-a-link",
  },
  {
    title: "Put your logo in the middle of a QR code",
    blurb: "How big it can safely be, and why the code still scans with a piece missing.",
    href: "/guides/qr-code-with-a-logo-in-the-middle",
  },
];

const FAQ: { q: string; a: ReactElement }[] = [
  {
    q: "Is it free?",
    a: (
      <>
        Yes. There is no account, no trial and no paid version. Hosting costs very little because
        your device does the actual work. Some pages may show a small ad to cover the rest. An ad
        can&rsquo;t see the files you open here.
      </>
    ),
  },
  {
    q: "Can you see my files?",
    a: (
      <>
        No. Each tool is a web page whose code runs in your browser. It reads the file on your
        device and writes the result back to your device. There is no server on our side that
        receives files. You can check this yourself in your browser&rsquo;s network panel while you
        use a tool. The one exception is described in the{" "}
        <a href="/privacy" className="link">privacy policy</a>: OtterDrop exchanges a few bytes of
        connection data so two devices can find each other. Never the files.
      </>
    ),
  },
  {
    q: "Do the tools work offline?",
    a: (
      <>
        OtterPDF, OtterStudio and OtterQR do. After the first visit they keep working with no
        connection. OtterDrop needs the devices to reach each other, so it needs a network, but not
        the internet. A home router or a phone hotspot is enough.
      </>
    ),
  },
  {
    q: "Which browsers?",
    a: (
      <>
        Current Chrome, Edge, Firefox and Safari, on desktop and mobile. It&rsquo;s all standard web
        APIs (WebRTC for OtterDrop, Web Workers for OtterPDF, WebCodecs for OtterStudio, plain SVG
        and canvas for OtterQR). The one gap: Firefox has no video encoder yet, so OtterStudio can
        edit there but exports only in Chrome, Edge and Safari. Nothing to install.
      </>
    ),
  },
  {
    q: "Is there a file-size limit?",
    a: (
      <>
        Only your device&rsquo;s memory. Nothing is uploaded, so there is no server-side cap.
        OtterPDF warns at 100&nbsp;MB per file because phones can run out of memory rendering very
        large scans. OtterDrop sends files in small chunks, so multi-gigabyte transfers work fine.
        OtterStudio plays whatever your browser can decode, 4K included; a long export just takes a
        while.
      </>
    ),
  },
  {
    q: "Who makes this?",
    a: (
      <>
        Two indie developers, in our spare time. There&rsquo;s a bit more on the{" "}
        <a href="/about" className="link">about page</a>. If something is broken or you have an idea,{" "}
        <a href="/contact" className="link">write to us</a>.
      </>
    ),
  },
];

/** What each tool leans on, for the "how it works" list. */
const STACK: { tool: string; uses: string }[] = [
  {
    tool: "OtterDrop",
    uses: "WebRTC, the same thing video calls use, so two devices talk straight to each other.",
  },
  {
    tool: "OtterPDF",
    uses: "pdf.js to draw the pages, pdf-lib to write the new file, both in a worker on the page.",
  },
  {
    tool: "OtterStudio",
    uses: "A canvas for every frame, then WebCodecs, the video encoder built into the browser.",
  },
  {
    tool: "OtterQR",
    uses: "An SVG your browser paints, and a canvas when you want that SVG as a PNG.",
  },
];

const NAV: { label: string; href: string }[] = [
  { label: "Tools", href: "#tools" },
  { label: "How it works", href: "#how" },
  { label: "Guides", href: "/guides/" },
  { label: "FAQ", href: "#faq" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export function App() {
  return (
    <div className="grain relative flex min-h-dvh flex-col bg-cream font-sans text-ink">
      <div className="relative z-10 mx-auto flex w-full max-w-[880px] flex-1 flex-col px-6 py-8 sm:px-8">
        {/* Top nav */}
        <nav aria-label="Site" className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-rule pb-4">
          <a href="/" className="eyebrow flex items-center gap-2 !text-pine">
            <img src="/logo.png" alt="" width="24" height="24" className="size-6" />
            ottercraft.dev
          </a>
          <ul className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] font-medium text-soft">
            {NAV.map((item) => (
              <li key={item.href}>
                <a href={item.href} className="transition-colors hover:text-pine">{item.label}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Hero */}
        <header className="relative mt-10">
          <img
            src="/otter-mascot.png"
            alt=""
            aria-hidden="true"
            className="bob pointer-events-none absolute -right-1 -top-2 hidden h-[128px] w-auto drop-shadow-[0_10px_12px_rgba(46,30,16,0.2)] sm:block"
          />
          <p className="eyebrow">Free · No account · Nothing uploaded</p>
          <h1 className="mt-3 max-w-[600px] font-serif text-[44px] font-semibold leading-[1.02] tracking-[-0.01em] text-pine sm:text-[60px] sm:leading-[0.98]">
            Tiny tools that <em className="text-teal">never upload your files.</em>
          </h1>
          <p className="mt-4 max-w-[560px] text-[16px] leading-[1.55] text-soft">
            Four of them so far. One moves files between your own devices, one fixes up and signs
            PDFs, one trims and layers video, and one makes QR codes. They all run inside your
            browser, so the files you open never leave your computer or phone. There is nothing to
            install and nothing to sign up for.
          </p>
        </header>

        {/* Tools */}
        <section id="tools" className="mt-12 scroll-mt-6">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="eyebrow">The tools</h2>
          </div>
          {/* Two across, never three: at this measure a third column turns every
              card into a column of broken words. Four tools, two rows, even. */}
          <ul className="grid gap-5 sm:grid-cols-2">
            {TOOLS.map((tool) => (
              <li key={tool.name}>
                <a
                  href={tool.href}
                  className="group flex h-full flex-col rounded-[18px] border border-rule bg-surface p-6 shadow-[0_1px_0_var(--color-rule)] transition-all duration-200 hover:-translate-y-0.5 hover:border-pine/40 hover:shadow-[0_18px_34px_-22px_rgba(46,30,16,0.55)]"
                >
                  <span
                    className={`flex size-11 shrink-0 items-center justify-center rounded-[13px] border ${ACCENTS[tool.accent]}`}
                  >
                    {tool.icon}
                  </span>

                  <h3 className="mt-4 font-serif text-[25px] leading-none text-pine">{tool.name}</h3>
                  <p className="mt-2.5 text-[14.5px] font-medium leading-[1.45] text-ink">{tool.tagline}</p>
                  <p className="mt-1.5 text-[13.5px] leading-[1.55] text-soft">{tool.detail}</p>

                  <ul className="mt-4 flex flex-wrap gap-1.5">
                    {tool.does.map((thing) => (
                      <li
                        key={thing}
                        className="rounded-full border border-rule/80 bg-cream/60 px-2.5 py-1 text-[11.5px] leading-none text-soft"
                      >
                        {thing}
                      </li>
                    ))}
                  </ul>

                  <span className="mt-5 flex items-center justify-between gap-3 border-t border-rule/70 pt-3.5 text-[13px] font-semibold text-pine">
                    <span className="truncate font-mono text-[11px] font-normal tracking-[0.04em] text-muted">
                      {tool.host}
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1.5">
                      Open
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                        <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
                      </svg>
                    </span>
                  </span>
                </a>
              </li>
            ))}

            {/* The shape of the grid says "four tools"; this says "and we are
                not finished". Full width so it closes the grid instead of
                leaving an odd gap, and dashed so nobody clicks it as a tool. */}
            <li className="sm:col-span-2">
              {/* Stacked on a phone: as a wrapping row, the middle column had a
                  shrinking basis and squeezed itself down to one word per line. */}
              <div className="flex flex-col items-start gap-4 rounded-[18px] border border-dashed border-rule bg-surface/40 p-5 sm:flex-row sm:items-center sm:gap-5">
                <img
                  src="/otter/otter-sleeping.png"
                  alt=""
                  aria-hidden="true"
                  className="h-14 w-auto shrink-0 opacity-90"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-[19px] leading-tight text-pine">More on the way</p>
                  <p className="mt-1 max-w-[46ch] text-[13.5px] leading-[1.55] text-soft">
                    We add a tool when we need one ourselves. If something you do online shouldn&rsquo;t
                    need a sign up, tell us about it.
                  </p>
                </div>
                <a
                  href="/contact"
                  className="group inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-rule bg-surface px-4 text-[13px] font-semibold text-pine transition-colors hover:border-pine"
                >
                  Suggest a tool
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5" aria-hidden="true">
                    <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
                  </svg>
                </a>
              </div>
            </li>
          </ul>
        </section>

        {/* Ad — directly under the tools. Renders nothing until a client id, slot and consent all exist. */}
        <AdBanner
          format="responsive"
          slot={import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined}
          className="mx-auto mt-14 max-w-[728px]"
        />

        {/* How it works */}
        <section id="how" className="mt-14 scroll-mt-6">
          <div className="flex items-end justify-between gap-6">
            <div>
              <h2 className="eyebrow">How it works</h2>
              <p className="mt-3 max-w-[620px] font-serif text-[26px] leading-[1.15] text-pine sm:text-[30px]">
                Why nothing gets uploaded
              </p>
            </div>
            <img
              src="/otter/otter-at-laptop.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none hidden h-[104px] w-auto shrink-0 sm:block"
            />
          </div>
          <div className="mt-7 grid gap-x-8 gap-y-7 sm:grid-cols-3">
            <Step
              n="01"
              title="The page is the program"
              text="Your browser downloads a bit of code and runs it. That code opens your file, does the work and saves the result, all on your own machine."
            />
            <Step
              n="02"
              title="Nothing to log in to"
              text="Accounts exist so a company can hold your data for you. Nothing here leaves your device, so there is no login, no history and no files of yours to delete."
            />
            <Step
              n="03"
              title="Ordinary web technology"
              text="No plugins and no extensions. Each tool is built on something your browser already ships with."
            />
          </div>

          {/* The per-tool detail, out of the prose and into a list you can skim.
              As a third paragraph it was twice the length of the other two. */}
          <dl className="mt-9 grid border-t border-rule sm:grid-cols-2 sm:gap-x-10">
            {STACK.map((row) => (
              <div
                key={row.tool}
                className="flex flex-col gap-0.5 border-b border-rule py-3.5 sm:flex-row sm:items-baseline sm:gap-4"
              >
                <dt className="w-[6.5rem] shrink-0 font-serif text-[16px] leading-tight text-pine">
                  {row.tool}
                </dt>
                <dd className="text-[13.5px] leading-[1.55] text-soft">{row.uses}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Guides */}
        <section id="guides" className="mt-14 scroll-mt-6">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="eyebrow">Guides</h2>
            <a href="/guides/" className="text-[13px] font-semibold text-pine">All guides →</a>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {GUIDES.map((g) => (
              <li key={g.href}>
                <a
                  href={g.href}
                  className="flex h-full flex-col gap-1 rounded-[12px] border border-rule bg-surface/70 px-4 py-3.5 transition-colors hover:border-pine/40"
                >
                  <span className="font-serif text-[17px] leading-tight text-pine">{g.title}</span>
                  <span className="text-[13px] leading-[1.5] text-soft">{g.blurb}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-14 scroll-mt-6">
          <div className="flex items-end justify-between gap-6">
            <h2 className="eyebrow">Questions</h2>
            <img
              src="/otter/otter-laughing.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none -mb-1 hidden h-[74px] w-auto shrink-0 sm:block"
            />
          </div>
          <dl className="mt-4 divide-y divide-rule border-y border-rule">
            {FAQ.map((item) => (
              <div key={item.q} className="grid gap-1.5 py-4 sm:grid-cols-[220px_1fr] sm:gap-6">
                <dt className="font-serif text-[18px] leading-tight text-pine">{item.q}</dt>
                <dd className="text-[14px] leading-[1.6] text-soft">{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* About */}
        <section id="about" className="mt-14 flex scroll-mt-6 flex-col items-start gap-5 rounded-[14px] border border-rule bg-surface p-6 sm:flex-row sm:items-center sm:gap-8">
          <div className="min-w-0">
            <h2 className="eyebrow">Why we made this</h2>
            <p className="mt-3 text-[15px] leading-[1.6] text-soft">
              We got tired of websites that want you to upload a PDF just to merge it, or to make an
              account to move a photo from a phone to a laptop. A browser can do these things on its
              own. So the two of us are building a few small tools that do, and we add one when we
              need it.
            </p>
            <p className="mt-3 text-[14px]">
              <a href="/about" className="link font-semibold text-pine">More about the project →</a>
            </p>
          </div>
          <img
            src="/otter/otters-sharing-fish.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none mx-auto h-[104px] w-auto shrink-0 sm:mx-0"
          />
        </section>

        {/* Footer */}
        <footer className="mt-14 flex flex-col gap-4 border-t border-rule pt-6 sm:flex-row sm:items-start sm:justify-between">
          <ul className="flex flex-wrap gap-x-8 gap-y-2">
            <li className="flex items-baseline gap-2.5"><span className="font-mono text-[11px] text-teal">01</span><span className="text-[13px]">Runs in your browser</span></li>
            <li className="flex items-baseline gap-2.5"><span className="font-mono text-[11px] text-teal">02</span><span className="text-[13px]">No account</span></li>
            <li className="flex items-baseline gap-2.5"><span className="font-mono text-[11px] text-teal">03</span><span className="text-[13px]">Nothing uploaded</span></li>
          </ul>
          <div className="flex flex-col gap-1.5 text-[12px] text-muted sm:items-end">
            <p className="flex flex-wrap gap-4">
              <a href="/about" className="link">About</a>
              <a href="/guides/" className="link">Guides</a>
              <a href="/contact" className="link">Contact</a>
              <a href="/privacy" className="link">Privacy</a>
              <a href="/terms" className="link">Terms</a>
              <AdConsentLink />
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[11px] tracking-[0.04em] hover:text-pine">
              {CONTACT_EMAIL}
            </a>
            <SocialLinks className="mt-1" />
          </div>
        </footer>
      </div>
    </div>
  );
}

function Step({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[11px] text-teal">{n}</span>
      <h3 className="font-serif text-[19px] leading-tight text-pine">{title}</h3>
      <p className="text-[13.5px] leading-[1.6] text-soft">{text}</p>
    </div>
  );
}
